<script setup>
import interactjs from "interactjs";
import { useStore } from "vuex";
import { computed, ref, onMounted, watch } from "vue";

const emit = defineEmits(["update:resize", "end:resize"]);

const designReference = ref();
const designReferenceHandler = ref();
const store = useStore();
const artworkUrl = computed(() => store.getters["artwork/artworkUrl"]);
const artworkSize = computed(() => store.getters["artwork/artworkSize"]);
const surface = computed(() => store.getters["artwork/surface"]);
const downscaleFactor = computed(() => store.getters["artwork/downscaleFactor"]);
const style = ref(null);
const interact = ref(null);

const calculateAspectRatio = url =>
  new Promise(resolve => {
    const image = new Image();
    image.src = url;
    image.onload = () => {
      const ratio = image.width / image.height;
      resolve(ratio);
    };
  });

const initDesign = async () => {
  const ratio = await calculateAspectRatio(artworkUrl.value);
  const oneInch = surface.value.ppi * downscaleFactor.value;
  const minimalResizeImage =
    ratio > 1
      ? { width: oneInch * ratio, height: oneInch }
      : { width: oneInch, height: oneInch / ratio };

  interact.value = interactjs(designReferenceHandler.value).resizable({
    edges: { left: true, right: true, bottom: true, top: true },
    listeners: {
      move(event) {
        const { width, height } = event.rect;
        style.value = { width: `${width}px`, height: `${height}px` };
        emit("update:resize");
      },
      end() {
        store.dispatch("artwork/updateArtworkSize", interact.value.getRect(designReference.value));
        store.commit("checkout/setCurrentPlacementAction", "size_change");
        emit("end:resize");
      }
    },
    restrictEdges: { outer: "parent", endOnly: true },
    modifiers: [
      interactjs.modifiers.aspectRatio({
        ratio,
        modifiers: [interactjs.modifiers.restrictSize({ max: "parent" })]
      }),
      interactjs.modifiers.restrictSize({ min: minimalResizeImage })
    ]
  });
  store.dispatch("artwork/initArtworkSize", interact.value.getRect(designReference.value));
};

watch(artworkSize, size => {
  if (size.width && size.height) {
    style.value = {
      width: `${size.width}px`,
      height: `${size.height}px`
    };
  } else {
    style.value = null;
  }
});
watch(artworkUrl, async (newUrl, prevUrl) => {
  const ratio = await calculateAspectRatio(newUrl);
  const prevRatio = await calculateAspectRatio(prevUrl);
  if (ratio === prevRatio && newUrl === prevUrl) return; // It's guaranteed that size will be the same, no need to recalculate

  store.commit("artwork/clearArtworkSize");
  await initDesign();
});
onMounted(initDesign);
</script>

<template>
  <img
    ref="designReference"
    class="artwork-design-img"
    :src="artworkUrl"
    :style="style"
    alt="Artwork canvas"
  />
  <div ref="designReferenceHandler" class="artwork-design-border" :style="style">
    <div class="artwork-design-border__handle artwork-design-border__handle--left" />
    <div class="artwork-design-border__handle artwork-design-border__handle--right" />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-design-img {
  user-select: none;
  margin: 0 auto;
  touch-action: none;
  max-height: 100%;
  pointer-events: none;
}

.artwork-design-border {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  outline: 2px solid $ultramarine-blue;
  touch-action: none;

  &::before {
    position: absolute;
    inset: 0;
    margin: -20px;
    content: "";

    @include with-desktop {
      display: none;
    }
  }

  &__handle {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid $ultramarine-blue;
    background-color: $white;
    bottom: -11px;

    &--left {
      left: -11px;
    }

    &--right {
      right: -11px;
    }
  }
}
</style>
